import { useLocalization } from '../useLocalization';
import { useRouteExtended } from '../useRouteExtended';
import { ROUTES } from '~~/constants/routes';
import {
  getNameForRoute,
  isHomePageSlug,
  isLoginPageSlug,
  isProductPageSlug,
  isSearchPageSlug,
  isValidCmsPage,
  isNewsletterPageSlug,
  isNewsletterVoucherPageSlug,
} from '~~/helpers/routes/getPageInformation';

export const useIsPage = () => {

  const route = useRoute();
  const { languageAndCountry } = useLocalization();
  const { getAdjustedSlug } = useRouteExtended();
  const { publishedPages } = useDynamicPublishedCmsPages();

  /**
   *  Determine if the current route is the same as the page name arg
   */
  const isOnPage = (pageName: keyof typeof ROUTES): boolean =>
    !!route.name?.startsWith(getNameForRoute(pageName.toString()));
  const isOnProductPage: ComputedRef<boolean> = computed(() =>
    !!(route.params?.slug && isProductPageSlug(route.params?.slug)));
  const isOnHomePage: ComputedRef<boolean> = computed(() => {

    return isHomePageSlug(route.path);
  
  });
  const isOnInternalCmsPage: ComputedRef<boolean> = computed(() => {

    return isValidCmsPage({
      slug: route?.params?.slug,
      languageAndCountry: languageAndCountry.value,
      publishedPages: publishedPages.value,
    });

  });
  const isOnSearchPage: ComputedRef<boolean> = computed(() => isSearchPageSlug(route.path));
  const isOnPaymentPage: ComputedRef<boolean> = computed(() =>
    route.path === getAdjustedSlug(ROUTES.CHECKOUT_PAYMENT));
  const isCheckoutPage: ComputedRef<boolean> = computed(() => isOnPage('CHECKOUT'));
  const isOnAddressesPage: ComputedRef<boolean> = computed(() =>
    route.path === getAdjustedSlug(ROUTES.CHECKOUT_ADDRESSES));
  const isThankYouPage = computed(() => route.path === getAdjustedSlug(ROUTES.THANK_YOU));
  const isCheckoutLogin = computed(() => route.path === getAdjustedSlug(ROUTES.CHECKOUT_LOGIN));
  const isLoginPage = computed(() => isLoginPageSlug(route.path));
  const isCheckoutCreateAccount = computed(
    () => route.path === getAdjustedSlug(ROUTES.CHECKOUT_CREATE_ACCOUNT),
  );
  const isOnNewsletterConfirmationPage: ComputedRef<boolean> = computed(
    () => isNewsletterPageSlug(route.path) || isNewsletterVoucherPageSlug(route.path),
  );
  const isOnPartnersPage: ComputedRef<boolean> = computed(() =>
    [ROUTES.EXPONDO_PARTNERS, ROUTES.MY_EXPONDO_PARTNERS].some(routeKey => route.path === getAdjustedSlug(routeKey)));

  return {
    isOnProductPage,
    isOnHomePage,
    isOnSearchPage,
    isOnPage,
    isOnPaymentPage,
    isOnAddressesPage,
    isOnInternalCmsPage,
    isCheckoutPage,
    isThankYouPage,
    isCheckoutLogin,
    isCheckoutCreateAccount,
    isLoginPage,
    isOnNewsletterConfirmationPage,
    isOnPartnersPage,
  };

};
